import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button } from 'reactstrap';
import { MMY_TAB_ID } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tab-constants';
import { APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID } from 'site-modules/shared/constants/appraisal/appraisal';
import { FindVinModal } from 'site-modules/shared/components/find-vin-modal/find-vin-modal';
import {
  useAppraisalTabsConfig,
  useAppraisalTabsState,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';
import { AppraisalTabLink } from './appraisal-tab-link';

/**
 * Mixed consumers — they need both config flags and activeTabId/tabs from state. They use both hooks separately rather than the convenience useAppraisalTabsContext() to keep the intent explicit.
 */
export function TabsCopy({
  tabLinks,
  onTabLinkClick,
  onVinLookupBtnClick = undefined,
  addressToVinDrawer = null,
  hasAddressToVinDrawer = false,
}) {
  const {
    renderAddressTab,
    isEmoSearchEnabled,
    isAppraisalLanding,
    isSellCarLanding,
    linksColorClassName,
    accentColorClassName,
    accentBgColorClassName,
  } = useAppraisalTabsConfig();
  const { activeTabId } = useAppraisalTabsState();
  const landingTextClass = classnames({ 'text-cool-gray-40': isAppraisalLanding });

  return (
    <Fragment>
      <div className="edmunds-tip">
        <div
          className={classnames(
            'align-bottom me-0_5 align-items-center bg-transparent size-14 fw-normal text-primary-darker',
            isEmoSearchEnabled && isSellCarLanding ? 'd-flex' : 'd-inline-flex'
          )}
        >
          <i className="icon-edmunds-car me-0_25 size-16" aria-hidden />
          <span className={classnames('fw-medium', accentColorClassName)}>Edmunds Tip</span>
        </div>
        <span>
          <span className={landingTextClass}>By entering your </span>
          {activeTabId === MMY_TAB_ID ? (
            <AppraisalTabLink className={linksColorClassName} onClick={onTabLinkClick} tabLink={tabLinks.FIRST} />
          ) : (
            <span className={landingTextClass}>{tabLinks.FIRST.label}</span>
          )}
          <span className={landingTextClass}>{' or '}</span>
          <AppraisalTabLink className={linksColorClassName} onClick={onTabLinkClick} tabLink={tabLinks.SECOND} />
          <span className={landingTextClass}>
            , you&apos;ll get a more accurate appraisal that may raise your car&apos;s value.
          </span>
        </span>
      </div>

      {!renderAddressTab &&
        (hasAddressToVinDrawer ? (
          addressToVinDrawer
        ) : (
          <FindVinModal
            isBackgroundStyleTabEnabled
            creativeId={APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID}
            openCtaAsButtonClassName={classnames(
              { 'ms-lg-0_5 mt-lg-0 d-block': isEmoSearchEnabled },
              accentBgColorClassName
            )}
            iconClassName={accentColorClassName}
          />
        ))}

      {renderAddressTab && (
        <Button
          color="custom"
          className="bg-blue-100 px-1 py-0_25 rounded-pill size-14 mt-0_5"
          onClick={onVinLookupBtnClick}
        >
          <i className="icon icon-search text-blue-40" aria-hidden />
          <span className="fw-normal text-gray-darker ms-0_25">Find my VIN</span>
        </Button>
      )}
    </Fragment>
  );
}

TabsCopy.propTypes = {
  tabLinks: PropTypes.shape({
    FIRST: PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      secondaryLinkLabel: PropTypes.string,
    }),
    SECOND: PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      secondaryLinkLabel: PropTypes.string,
    }),
  }).isRequired,
  onTabLinkClick: PropTypes.func.isRequired,
  onVinLookupBtnClick: PropTypes.func,
  addressToVinDrawer: PropTypes.node,
  hasAddressToVinDrawer: PropTypes.bool,
};
